import { useState } from 'react';
import { parseKmlBoundaries, boundaryToCotXml, type BoundaryContour, type BoundaryCategory } from '@vtak/shared';
import { apiClient } from '@/services/api-client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Category filter for the preview list */
type CategoryFilter = 'all' | BoundaryCategory;

/** Outcome of a publish run */
interface PublishResult {
  published: number;
  failed: number;
}

/**
 * Import a KML range-contour file, preview its boundaries, and publish selected
 * ones to TAK as CoT polygon shapes. Air/sea contours are toggleable.
 */
export function BoundaryImportTab(): React.JSX.Element {
  const [contours, setContours] = useState<BoundaryContour[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function onFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const parsed = parseKmlBoundaries(await file.text());
      setContours(parsed);
      setSelected(new Set(parsed.map((_, i) => i)));
      if (parsed.length === 0) {
        setError(`No boundary polygons found in "${file.name}" — is it a KML file with Placemark polygons?`);
      }
    } catch {
      setError(`Could not read "${file.name}".`);
    }
  }

  async function publish(): Promise<void> {
    setBusy(true);
    let published = 0;
    let failed = 0;
    for (const index of selected) {
      const contour = contours[index];
      if (!contour) continue;
      const res = await apiClient.sendCot(boundaryToCotXml(contour, `boundary-${index}`));
      if (res.ok) published += 1;
      else failed += 1;
    }
    setResult({ published, failed });
    setBusy(false);
  }

  const visible = contours
    .map((contour, index) => ({ contour, index }))
    .filter(({ contour }) => filter === 'all' || contour.category === filter);

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
      <FilePicker onFile={onFile} />
      {error && <p className="text-xs text-red-400">{error}</p>}
      {contours.length > 0 && <CategoryTabs filter={filter} onChange={setFilter} />}

      <div className="flex flex-1 flex-col gap-1">
        {visible.map(({ contour, index }) => (
          <ContourRow
            key={index}
            contour={contour}
            checked={selected.has(index)}
            onToggle={() => setSelected(toggle(selected, index))}
          />
        ))}
        {contours.length === 0 && !error && (
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Choose a KML file to preview and publish range boundaries.
          </p>
        )}
      </div>

      {contours.length > 0 && (
        <PublishBar
          count={selected.size}
          busy={busy}
          result={result}
          onPublish={publish}
        />
      )}
    </div>
  );
}

/** File input for the KML source. */
function FilePicker({ onFile }: { onFile: (file: File | undefined) => void }): React.JSX.Element {
  return (
    <label className="flex cursor-pointer items-center justify-center rounded-md border border-dashed border-echo-border py-3 text-xs text-muted-foreground transition-colors hover:border-echo-yellow/40 hover:text-foreground">
      <input
        type="file"
        accept=".kml,.xml"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      Choose KML file…
    </label>
  );
}

/** Air/sea/all category filter tabs. */
function CategoryTabs({
  filter,
  onChange,
}: {
  filter: CategoryFilter;
  onChange: (value: CategoryFilter) => void;
}): React.JSX.Element {
  const options: CategoryFilter[] = ['all', 'air', 'sea'];
  return (
    <div className="flex gap-1">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={cn(
            'flex-1 rounded px-2 py-1 text-[11px] font-semibold uppercase transition-colors',
            filter === option
              ? 'bg-echo-yellow/15 text-echo-yellow'
              : 'bg-white/[0.04] text-muted-foreground hover:text-foreground',
          )}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

/** A single previewed boundary row with a selection checkbox. */
function ContourRow({
  contour,
  checked,
  onToggle,
}: {
  contour: BoundaryContour;
  checked: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-echo-border bg-white/[0.02] px-2.5 py-2">
      <input type="checkbox" checked={checked} onChange={onToggle} className="h-3.5 w-3.5 accent-echo-yellow" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-foreground">{contour.name}</p>
        <p className="text-[10px] text-muted-foreground">{contour.points.length} vertices</p>
      </div>
      <CategoryChip category={contour.category} />
    </label>
  );
}

/** Air/sea category chip. */
function CategoryChip({ category }: { category: BoundaryCategory }): React.JSX.Element {
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
        category === 'sea' ? 'bg-sky-500/15 text-sky-300' : 'bg-amber-500/15 text-amber-300',
      )}
    >
      {category}
    </span>
  );
}

/** Publish action bar with result feedback. */
function PublishBar({
  count,
  busy,
  result,
  onPublish,
}: {
  count: number;
  busy: boolean;
  result: PublishResult | null;
  onPublish: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-t border-echo-border pt-3">
      {result && (
        <p className="text-xs text-muted-foreground">
          Published {result.published} boundary{result.published === 1 ? '' : 'ies'}
          {result.failed > 0 ? `, ${result.failed} failed` : ''}.
        </p>
      )}
      <Button size="sm" disabled={busy || count === 0} onClick={onPublish}>
        {busy ? 'Publishing…' : `Publish ${count} selected`}
      </Button>
    </div>
  );
}

/** Return a new Set with the index toggled. */
function toggle(set: Set<number>, index: number): Set<number> {
  const next = new Set(set);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  return next;
}
